import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { createClient } from '@/lib/supabase/client';
import { Plus, Pencil, Trash } from 'lucide-react';

interface LessonForm {
  title: string;
  description: string;
  video_url: string;
  order: number;
}

export default function AulasPage() {
  const { id: courseId } = useParams();
  const supabase = createClient();
  const [aulas, setAulas] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<LessonForm>({
    title: '',
    description: '',
    video_url: '',
    order: 1,
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAulas = async () => {
      setLoading(true);
      const { data } = await supabase.from('lessons').select('*').eq('course_id', courseId).order('order');
      setAulas(data || []);
      setLoading(false);
    };
    fetchAulas();
  }, [courseId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      course_id: courseId,
      order: Number(form.order) || 1,
    };
    if (editId) {
      await supabase.from('lessons').update(payload).eq('id', editId);
    } else {
      await supabase.from('lessons').insert(payload);
    }
    setOpen(false);
    setForm({ title: '', description: '', video_url: '', order: 1 });
    setEditId(null);
    setLoading(false);
    // Recarregar lista
    const { data } = await supabase.from('lessons').select('*').eq('course_id', courseId).order('order');
    setAulas(data || []);
  };

  const handleEdit = (aula: any) => {
    setForm({
      title: aula.title || '',
      description: aula.description || '',
      video_url: aula.video_url || '',
      order: aula.order || 1,
    });
    setEditId(aula.id);
    setOpen(true);
  };

  const handleDelete = async (aulaId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta aula?')) return;
    setLoading(true);
    await supabase.from('lessons').delete().eq('id', aulaId);
    setAulas((prev) => prev.filter((a) => a.id !== aulaId));
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ title: '', description: '', video_url: '', order: 1 });
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Aulas do Curso</h2>
        <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : handleClose()}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditId(null); setForm({ title: '', description: '', video_url: '', order: aulas.length + 1 }); }}>
              <Plus className="mr-2 h-4 w-4" /> Nova Aula
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editId ? 'Editar Aula' : 'Nova Aula'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Título</label>
                <Input
                  name="title"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrição</label>
                <Input
                  name="description"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vídeo (URL)</label>
                <Input
                  name="video_url"
                  value={form.video_url}
                  onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ordem</label>
                <Input
                  name="order"
                  type="number"
                  min={1}
                  value={form.order}
                  onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose}>Cancelar</Button>
                <Button type="submit" disabled={loading}>{editId ? 'Salvar' : 'Criar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-4">
        {aulas.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">Nenhuma aula cadastrada ainda.</div>
        ) : (
          aulas.map((aula) => (
            <div key={aula.id} className="rounded-lg border p-4 flex flex-col md:flex-row md:items-center gap-4 bg-white shadow-sm">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-lg">{aula.title}</span>
                  <span className="text-xs text-muted-foreground">Ordem: {aula.order}</span>
                </div>
                <div className="text-sm text-muted-foreground mb-1">{aula.description}</div>
                {aula.video_url && (
                  <a href={aula.video_url} target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm">Ver vídeo</a>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(aula)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(aula.id)}><Trash className="w-4 h-4" /></Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
} 